import type { TranslationKey } from '@/i18n-key';
import type { Objective, ObjectiveOrDraft } from '@/types/objectives';

import { ObjectiveStatus as StoredObjectiveStatus } from '@/constants/ObjectiveStatus';
import { toIsoDate } from '@/utils/dateUtils';

/** An objective reaches its goal at 100 %; nothing beyond changes its state. */
const COMPLETE_PROGRESSION = 100;

/**
 * How much slack the pace rule leaves. Keeping up with time means being at 50 % when half the
 * window has gone; an objective is only called out below *half* that pace.
 */
const PACE_TOLERANCE = 2;

/**
 * How much of its window an objective must have spent before the pace rule says anything about
 * it. Below it, a slow start is a start: an objective opened on Monday at 0 % would otherwise be
 * called out on Tuesday, when nobody could reasonably have moved it yet.
 */
const AT_RISK_MIN_ELAPSED_SHARE = 0.5;

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The four states an objective can read as, in the order they take precedence. None of them is
 * stored: they are derived from the progression and the objective's own window, so every screen
 * showing an objective tells the same story.
 *
 * `atRisk` and `overdue` cannot both apply: being at risk only means anything while there is still
 * time, and being overdue only once there is none.
 */
export type ObjectiveStatus = 'completed' | 'overdue' | 'atRisk' | 'onTrack';

type ObjectiveWindow = Pick<Objective, 'startDate' | 'endDate' | 'progression'>;

/** Parses a `'YYYY-MM-DD'` date as a day, not an instant: same base for both sides of a diff. */
const toDayNumber = (isoDate: string): number => Date.parse(`${isoDate}T00:00:00Z`);

interface ElapsedShareOfWindowOptions {
    startDate: string;
    endDate: string;
    today: string;
}

/**
 * The share of the objective's window already gone, from 0 to 1. A window that has not started
 * reads as 0, one that is over reads as 1.
 */
const elapsedShareOfWindow = ({
    startDate,
    endDate,
    today,
}: ElapsedShareOfWindowOptions): number | null => {
    const start = toDayNumber(startDate);
    const end = toDayNumber(endDate);
    const windowLength = (end - start) / MILLISECONDS_PER_DAY;

    // A one-day objective (or one whose dates were saved out of order) has no window to measure a
    // pace against: dividing by it would be a division by zero. It simply never reads as at risk —
    // the day it ends, `overdue` takes over.
    if (!Number.isFinite(windowLength) || windowLength <= 0) {
        return null;
    }

    const elapsed = (toDayNumber(today) - start) / MILLISECONDS_PER_DAY;

    return Math.min(Math.max(elapsed / windowLength, 0), 1);
};

/**
 * What state an objective reads as today.
 *
 * The single place this is decided: every screen that shows an objective's state calls it, so
 * none can invent a shade of its own. It reads the dates carried by the **objective**, never those
 * of the period it is filed under: an objective keeps its own window even when attached to a wider
 * period, so the period's dates would measure someone else's pace.
 *
 * Precedence: reaching 100 % wins over everything, then a passed due date, then the pace rule —
 * below half the pace time has set, the objective is called out while it can still be caught up.
 * The pace rule only opens once half the window has gone: before that, nothing is concluded from
 * a progression that has had no time to move.
 */
export const getObjectiveStatus = (
    objective: ObjectiveWindow,
    // The viewer's local calendar day, not the UTC one: in the evening west of Greenwich the UTC
    // day is already tomorrow and would mark an objective due today as overdue.
    today: string = toIsoDate(new Date()),
): ObjectiveStatus => {
    if (objective.progression >= COMPLETE_PROGRESSION) {
        return 'completed';
    }

    if (objective.endDate < today) {
        return 'overdue';
    }

    const elapsedShare = elapsedShareOfWindow({
        startDate: objective.startDate,
        endDate: objective.endDate,
        today,
    });
    if (elapsedShare === null || elapsedShare < AT_RISK_MIN_ELAPSED_SHARE) {
        return 'onTrack';
    }

    const expectedPace = (elapsedShare * COMPLETE_PROGRESSION) / PACE_TOLERANCE;

    return objective.progression < expectedPace ? 'atRisk' : 'onTrack';
};

/** The five states a list of objectives can be read or narrowed by: the four derived ones, plus the
 *  stored draft. */
export type ObjectiveStatusOrDraft = ObjectiveStatus | 'draft';

/** The states a list of objectives can be narrowed by, in the order its filter offers them: the
 *  stored draft first, then the four derived ones. */
export const OBJECTIVE_STATUS_FILTER_VALUES = [
    'draft',
    'onTrack',
    'atRisk',
    'overdue',
    'completed',
] as const satisfies readonly ObjectiveStatusOrDraft[];

/** The state `value` names, or `undefined` when it names none. */
export const asObjectiveStatusOrDraft = (value: string): ObjectiveStatusOrDraft | undefined =>
    OBJECTIVE_STATUS_FILTER_VALUES.find((status) => status === value);

/** The states `values` name, in their order; a value that names none is left out. */
export const pickObjectiveStatuses = (values: string[]): ObjectiveStatusOrDraft[] =>
    values
        .map(asObjectiveStatusOrDraft)
        .filter((status): status is ObjectiveStatusOrDraft => !!status);

/**
 * What state an objective reads as when drafts sit in the same list as published objectives.
 *
 * A draft has no pace to keep nor deadline to miss yet: it only ever reads as a draft, and the
 * derived rule above never runs on it.
 */
export const getObjectiveStatusOrDraft = (objective: ObjectiveOrDraft): ObjectiveStatusOrDraft =>
    objective.status === StoredObjectiveStatus.DRAFT ? 'draft' : getObjectiveStatus(objective);

/**
 * How each state is named, wherever an objective's state reads: its card, its page, the state
 * filter. One name per state, so no screen can call the same objective by another.
 */
export const OBJECTIVE_STATUS_LABEL_KEYS: Record<ObjectiveStatusOrDraft, TranslationKey> = {
    draft: 'common.draft',
    onTrack: 'objectives.filters.onTrack',
    atRisk: 'objectives.card.atRisk',
    overdue: 'objectives.card.overdue',
    completed: 'objectives.detail.achieved',
};

/** Whether an objective is published, as opposed to a draft its creator is still writing. */
export const isPublishedObjective = (objective: ObjectiveOrDraft): objective is Objective =>
    objective.status !== StoredObjectiveStatus.DRAFT;

/**
 * Whether opening an objective leads to its form rather than its page: a draft, for whoever may
 * write it. Anyone else who sees a draft reads it on its page, without changing it.
 */
export const opensOnItsForm = (objective: ObjectiveOrDraft): boolean =>
    !isPublishedObjective(objective) && objective.canEdit;
